import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Alert, Button, Card, Field, Select, Spinner } from "@empite/ui-components";
import type { ButtonVariant } from "@empite/ui-components";
import type { Reason } from "../admin-client";
import { useReasonVocabulary } from "../admin-hooks";
import { ErrorAlert } from "./guards";

/**
 * The reason prompt — the one way a mutation leaves this portal.
 *
 * Every unsafe request to /admin/v1 must carry X-Admin-Reason-Code, and the
 * Admin API enforces that on a middleware over the whole resource subtree:
 * a mutation without a valid reason is rejected before it reaches a handler.
 * THE SERVER IS THE CONTROL. This component exists so an operator meets that
 * rule as a two-field dialog instead of as a 400, and so no screen in this
 * portal has a code path that calls a mutation without one — a button hands
 * its work to `ask`, and `ask` is the only thing holding the function that
 * runs it.
 *
 * The vocabulary is fetched rather than hard-coded because the closed set
 * lives in internal/admin/reason.go; the point of a closed set is that "who
 * touched forty accounts last Tuesday, and why" is a question the audit trail
 * can group and answer, which free text cannot.
 */

export interface ReasonedMutation {
  /** What the dialog is titled: "Suspend this account". */
  title: string;
  /** What will actually happen, in the operator's words. Shown above the
   *  picker because the reason is being recorded against exactly this. */
  description?: ReactNode;
  confirmLabel?: string;
  /**
   * How the confirm button is dressed. "danger" is the default because most
   * mutations in this console end something, and an operator should feel the
   * weight of the ones that do.
   *
   * "primary" is for the reversible ones — a device sign-out, which the person
   * undoes by signing in again. Dressing those in red teaches operators to
   * click through red, which is exactly the habit the destructive dialogs
   * depend on them NOT having. The reason picker itself stays mandatory either
   * way: the server requires the code, and this only changes the colour.
   */
  tone?: "danger" | "primary";
  /** Runs once a valid reason exists. Rejecting keeps the dialog open with
   *  the error, so the operator does not lose the note they typed. */
  run: (reason: Reason) => Promise<void>;
}

type AskFn = (mutation: ReasonedMutation) => void;

const ReasonPromptContext = createContext<AskFn | null>(null);

export function useReasonPrompt(): AskFn {
  const ask = useContext(ReasonPromptContext);
  if (!ask) {
    throw new Error("useReasonPrompt: no ReasonPromptProvider above this component.");
  }
  return ask;
}

/**
 * ReasonPromptProvider holds the pending mutation for the whole console.
 *
 * One provider around the layout rather than one dialog per screen: a second
 * copy of this component is a second answer to "may this go through without a
 * reason", and only one of the two would get updated.
 */
export function ReasonPromptProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ReasonedMutation | null>(null);

  return (
    <ReasonPromptContext.Provider value={setPending}>
      {children}
      {pending ? <ReasonDialog mutation={pending} onClose={() => setPending(null)} /> : null}
    </ReasonPromptContext.Provider>
  );
}

/**
 * ReasonButton is a button that cannot fire its mutation directly — it can
 * only ask for it. Screens use this instead of Button for anything that
 * writes, which is what keeps the rule above true by construction.
 */
export function ReasonButton({
  mutation,
  variant = "secondary",
  busy = false,
  disabled = false,
  children,
}: {
  mutation: ReasonedMutation;
  variant?: ButtonVariant;
  busy?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  const ask = useReasonPrompt();
  return (
    <Button variant={variant} busy={busy} disabled={disabled} onClick={() => ask(mutation)}>
      {children}
    </Button>
  );
}

/** "fraud_investigation" is what the server stores; "Fraud investigation" is
 *  what an operator is choosing between at 2am. */
function codeLabel(code: string): string {
  const words = code.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function ReasonDialog({ mutation, onClose }: { mutation: ReasonedMutation; onClose: () => void }) {
  const vocabulary = useReasonVocabulary();
  // No default code. Preselecting the first one makes it the reason for
  // everything anybody was in a hurry about, and an audit trail where every
  // row says the same thing records nothing.
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [touched, setTouched] = useState(false);

  // Escape closes, because the alternative to an easy cancel is an operator
  // picking any code to make the dialog go away.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const codes = vocabulary.data?.reason_codes ?? [];
  const maxNote = vocabulary.data?.max_note_chars ?? 500;
  const noteRequired = code !== "" && (vocabulary.data?.note_required ?? []).includes(code);
  const trimmedNote = note.trim();

  // The same three rules the server applies, checked here only so the
  // operator hears them before the request rather than after it.
  const codeError = touched && code === "" ? "Choose why you are doing this." : undefined;
  const noteError =
    touched && noteRequired && trimmedNote === ""
      ? `A note is required when the reason is "${codeLabel(code)}".`
      : trimmedNote.length > maxNote
        ? `Keep the note to ${maxNote} characters.`
        : undefined;
  const valid = code !== "" && !(noteRequired && trimmedNote === "") && trimmedNote.length <= maxNote;

  async function confirm() {
    setTouched(true);
    if (!valid || !vocabulary.data) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await mutation.run({
        code,
        note: trimmedNote === "" ? undefined : trimmedNote,
        // The header names come from the vocabulary document, so renaming
        // them server-side does not need a portal release.
        codeHeader: vocabulary.data.code_header,
        noteHeader: vocabulary.data.note_header,
      });
    } catch (err) {
      setError(err);
      return;
    } finally {
      setBusy(false);
    }
    onClose();
  }

  return (
    <div className="esm-reason__backdrop" role="presentation">
      <div
        className="esm-reason__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={mutation.title}
      >
        <Card title={mutation.title}>
          {mutation.description ? <div className="esm-reason__what">{mutation.description}</div> : null}
          <Alert tone="info" title="This change is recorded">
            The reason and note below are written to the admin audit trail against your operator
            account, and the server refuses the change without them.
          </Alert>
          <ErrorAlert error={vocabulary.error} title="Could not load the reason codes" />
          <ErrorAlert error={error} title="That change did not go through" />
          {vocabulary.isPending ? (
            <Spinner label="Loading the reason codes" />
          ) : (
            <>
              <Field label="Reason" htmlFor="reason-code" error={codeError}>
                <Select
                  id="reason-code"
                  // Focused on open: the dialog interrupts an operator
                  // mid-task, and the first thing it asks for should be the
                  // first thing their keyboard is in.
                  autoFocus
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                    setTouched(true);
                  }}
                >
                  <option value="">Choose a reason…</option>
                  {codes.map((value) => (
                    <option key={value} value={value}>
                      {codeLabel(value)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={noteRequired ? "Note (required)" : "Note (optional)"}
                htmlFor="reason-note"
                hint={`Up to ${maxNote} characters. A ticket number is worth more here than a sentence.`}
                error={noteError}
              >
                <textarea
                  id="reason-note"
                  className="esm-input esm-reason__note"
                  rows={3}
                  maxLength={maxNote}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>
            </>
          )}
          <div className="esm-actions">
            <Button
              variant={mutation.tone ?? "danger"}
              busy={busy}
              disabled={!valid}
              onClick={() => void confirm()}
            >
              {mutation.confirmLabel ?? "Confirm"}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
